import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchSocialMedia } from "../../api/controller/admin/adminSocialMedia";
import {
  fetchSocialMediaUpdatesBySocialMediaId,
  createSocialMediaUpdate,
  updateSocialMediaUpdate,
  deleteSocialMediaUpdate,
} from "../../api/controller/admin/adminSocialMediaUpdatesController";
import AdminSocialMediaUpdateDialog from "./adminDialogBoxes/AdminSocialMediaUpdateDialog";
import AdminConfirmDialog from "./adminDialogBoxes/AdminConfirmDialog";
import { useFlashMessage } from "../../components/FlashMessageProvider";

const AdminSocialMediaUpdates = () => {
  const navigate = useNavigate();
  const [socialList, setSocialList] = useState([]);
  const [selectedPlatform, setSelectedPlatform] = useState("");
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogInitial, setDialogInitial] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const { showMessage } = useFlashMessage();

  const userType = localStorage.getItem("userType");
  const isAdmin = userType === "admin";

  const dedupeUpdates = useCallback((items) => {
    if (!Array.isArray(items)) return [];
    const unique = new Map();
    items.forEach((item, index) => {
      if (!item) return;
      const primaryKey = item._id || item.id;
      const fallbackKey = `${item.__socialMediaId || ""}-${item.description || ""}-${item.createdAt || ""}`;
      const computed = primaryKey ? primaryKey.toString() : fallbackKey;
      const key = computed && computed.trim() ? computed.trim() : `fallback-${index}`;

      if (!unique.has(key)) {
        unique.set(key, item);
        return;
      }

      const existing = unique.get(key);
      const existingTimestamp = existing?.createdAt ? new Date(existing.createdAt).getTime() : 0;
      const incomingTimestamp = item.createdAt ? new Date(item.createdAt).getTime() : 0;
      if (incomingTimestamp > existingTimestamp) {
        unique.set(key, item);
      } else if (!existing.__socialMediaId && item.__socialMediaId) {
        unique.set(key, item);
      }
    });
    return Array.from(unique.values());
  }, []);

  useEffect(() => {
    let mounted = true;
    const loadSocials = async () => {
      if (!isAdmin) {
        if (mounted) {
          setSocialList([]);
          setLoading(false);
        }
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const socials = await fetchSocialMedia();
        if (mounted) setSocialList(socials || []);
      } catch (err) {
        console.error(err);
        if (mounted) setError('Failed to load social media list.');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    loadSocials();
    return () => { mounted = false; };
  }, [isAdmin]);

  const platformOptions = useMemo(() => {
    const uniques = new Set();
    (Array.isArray(socialList) ? socialList : []).forEach((item) => {
      if (!item || !item.platform) return;
      uniques.add(item.platform);
    });
    return Array.from(uniques);
  }, [socialList]);

  const filteredSocials = useMemo(() => {
    if (!selectedPlatform) return Array.isArray(socialList) ? socialList : [];
    return (Array.isArray(socialList) ? socialList : []).filter(
      (item) => (item?.platform || '') === selectedPlatform
    );
  }, [socialList, selectedPlatform]);

  useEffect(() => {
    let mounted = true;
    const loadUpdates = async () => {
      if (!isAdmin) {
        if (mounted) {
          setUpdates([]);
          setLoading(false);
        }
        return;
      }
      const targets = (Array.isArray(filteredSocials) ? filteredSocials : []).map((item) => item && (item._id || item.id)).filter(Boolean);
      if (!targets.length) {
        if (mounted) {
          setUpdates([]);
          setLoading(false);
        }
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const results = await Promise.all(targets.map(async (id) => {
          try {
            const res = await fetchSocialMediaUpdatesBySocialMediaId(id.toString());
            if (!Array.isArray(res)) return [];
            return res.map((item) => ({ ...item, __socialMediaId: id.toString() }));
          } catch (error) {
            console.error(error);
            return [];
          }
        }));
        const merged = dedupeUpdates(results.flat());
        if (mounted) {
          setUpdates(merged);
          setError(null);
        }
      } catch (err) {
        console.error(err);
        if (mounted) setError('Failed to load updates.');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    loadUpdates();
    return () => { mounted = false; };
  }, [isAdmin, filteredSocials, dedupeUpdates]);

  const refresh = async () => {
    if (!isAdmin) {
      setUpdates([]);
      return;
    }
    const targets = (Array.isArray(filteredSocials) ? filteredSocials : []).map((item) => item && (item._id || item.id)).filter(Boolean);
    if (!targets.length) {
      setUpdates([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const results = await Promise.all(targets.map(async (id) => {
        try {
          const res = await fetchSocialMediaUpdatesBySocialMediaId(id.toString());
          if (!Array.isArray(res)) return [];
          return res.map((item) => ({ ...item, __socialMediaId: id.toString() }));
        } catch (error) {
          console.error(error);
          return [];
        }
      }));
      const merged = dedupeUpdates(results.flat());
      setUpdates(merged);
    } catch (err) {
      console.error(err);
      setError('Failed to refresh updates.');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    if (!filteredSocials.length) {
      showMessage('Add a social media account first.', { type: 'info' });
      return;
    }
    setDialogInitial(null);
    setDialogOpen(true);
  };

  const handleEdit = (item) => {
    setDialogInitial(item);
    setDialogOpen(true);
  };

  const handleSave = async (payload) => {
    setIsSaving(true);
    try {
      const isEditing = Boolean(dialogInitial && (dialogInitial._id || dialogInitial.id));
      if (isEditing) {
        const updateId = (dialogInitial._id || dialogInitial.id).toString();
        await updateSocialMediaUpdate(updateId, payload);
      } else {
        await createSocialMediaUpdate(payload);
      }
      await refresh();
      showMessage(isEditing ? 'Social media update updated.' : 'Social media update added.', { type: 'success' });
      setDialogOpen(false);
      setDialogInitial(null);
    } catch (err) {
      console.error(err);
      showMessage('Failed to save update.', { type: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteRequest = (item) => {
    setDeleteTarget({ id: item._id || item.id, name: item.description });
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget?.id) return;
    setIsDeleting(true);
    try {
      await deleteSocialMediaUpdate(deleteTarget.id);
      await refresh();
      showMessage('Social media update deleted.', { type: 'success' });
      setIsDeleteDialogOpen(false);
      setDeleteTarget(null);
    } catch (err) {
      console.error(err);
      showMessage('Failed to delete update.', { type: 'error' });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    if (isDeleting) return;
    setIsDeleteDialogOpen(false);
    setDeleteTarget(null);
  };

  const getSocialMediaId = useCallback((update) => {
    if (!update) return '';
    const directTagged = update.__socialMediaId;
    if (directTagged) return directTagged.toString();
    const direct = update.socialMediaId || update.socialMediaID;
    if (direct) return direct.toString();

    const source = update.socialMedia;
    if (!source) return '';

    const matchFromList = (needle) => {
      if (!needle) return '';
      const str = needle.toString();
      const match = socialList.find((item) => {
        if (!item) return false;
        const itemId = item._id || item.id;
        if (itemId && itemId.toString() === str) return true;
        if (item.handle && item.handle === str) return true;
        if (item.platform && item.platform === str) return true;
        return false;
      });
      const id = match && (match._id || match.id);
      return id ? id.toString() : str;
    };

    if (typeof source === 'string') {
      return matchFromList(source);
    }

    if (typeof source === 'object') {
      const nestedId = source._id || source.id;
      if (nestedId) return nestedId.toString();
      if (source.handle) {
        const viaHandle = matchFromList(source.handle);
        if (viaHandle) return viaHandle;
      }
      if (source.platform) {
        const viaPlatform = matchFromList(source.platform);
        if (viaPlatform) return viaPlatform;
      }
    }
    return '';
  }, [socialList]);

  const getSocialMediaPlatform = useCallback((update) => {
    if (!update) return '';
    const lookupById = (id) => {
      if (!id) return '';
      const match = socialList.find((item) => {
        const itemId = item && (item._id || item.id);
        return itemId && itemId.toString() === id.toString();
      });
      return match?.platform || '';
    };

    const candidateId = getSocialMediaId(update);
    if (candidateId) {
      const resolved = lookupById(candidateId);
      if (resolved) return resolved;
    }

    const source = update.socialMedia;
    if (source && typeof source === 'object') {
      const platform = source.platform || source.name;
      if (platform) return platform;
    }

    return '';
  }, [socialList, getSocialMediaId]);

  const visibleUpdates = useMemo(() => {
    if (!Array.isArray(updates)) return [];
    const sorted = [...updates].sort((a, b) => {
      const aTime = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
      const bTime = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
      return bTime - aTime;
    });
    if (!selectedPlatform) return sorted;
    return sorted.filter((item) => {
      const platform = item?.socialMediaPlatform || getSocialMediaPlatform(item);
      return platform ? platform === selectedPlatform : false;
    });
  }, [updates, selectedPlatform, getSocialMediaPlatform]);

  const defaultSocialId = useMemo(() => {
    const first = filteredSocials[0];
    if (!first) return '';
    const id = first._id || first.id;
    return id ? id.toString() : '';
  }, [filteredSocials]);

  const dialogSocialId = useMemo(() => {
    if (dialogInitial) {
      const resolved = getSocialMediaId(dialogInitial);
      if (resolved) return resolved;
    }
    return defaultSocialId;
  }, [dialogInitial, getSocialMediaId, defaultSocialId]);

  if (!isAdmin) {
    return (
      <div className="p-8 text-center">
        <h2 className="text-2xl font-bold mb-4">Access Denied</h2>
        <p>You do not have permission to view this page.</p>
        <button className="mt-4 px-4 py-2 bg-blue-600 text-white rounded" onClick={() => navigate("/")}>Go Home</button>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <h1 className="text-3xl font-bold mb-4">Admin — Social Media Updates</h1>

      <div className="mb-4 flex flex-wrap items-center gap-4">
        <select
          value={selectedPlatform}
          onChange={(e) => setSelectedPlatform(e.target.value)}
          className="border rounded p-2"
        >
          <option value="">All Platforms</option>
          {platformOptions.map((platform) => (
            <option key={platform} value={platform}>{platform}</option>
          ))}
        </select>
        <button
          onClick={handleAdd}
          className={`px-4 py-2 rounded text-white ${filteredSocials.length ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-300 cursor-not-allowed'}`}
          disabled={!filteredSocials.length}
        >
          Add Update
        </button>
      </div>

      {loading ? (
        <p className="text-gray-600">Loading updates...</p>
      ) : error ? (
        <p className="text-red-600">{error}</p>
      ) : visibleUpdates.length ? (
        <div className="space-y-4">
          {visibleUpdates.map((u, index) => {
            const platform = u.socialMediaPlatform || getSocialMediaPlatform(u);
            const key = (u._id || u.id || getSocialMediaId(u) || `${index}`).toString();
            return (
              <div key={key} className="bg-white p-4 rounded shadow flex items-start gap-4">
                {u.image ? (
                  <div className="w-20 h-20 flex-shrink-0 overflow-hidden rounded">
                    <img src={u.image} alt={u.description ? u.description.slice(0, 40) : 'update'} className="w-full h-full object-cover" />
                  </div>
                ) : null}

                <div className="flex-1">
                  <p className="text-sm text-gray-600">{u.createdAt ? new Date(u.createdAt).toLocaleString() : ''}</p>
                  {platform ? <p className="text-sm text-gray-500">Platform: {platform}</p> : null}
                  <p className="mt-1 text-gray-800">{u.description}</p>
                  {u.embedUrl ? (
                    <a
                      href={u.embedUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 text-sm mt-2 inline-block"
                    >
                      View Embedded Post
                    </a>
                  ) : null}
                </div>

                <div className="flex items-start gap-3">
                  <button onClick={() => handleEdit(u)} className="text-blue-600">Edit</button>
                  <button onClick={() => handleDeleteRequest(u)} className="text-red-600">Delete</button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-gray-600">No updates found for the selected account yet.</p>
      )}

      <AdminSocialMediaUpdateDialog
        open={dialogOpen}
        initial={dialogInitial}
        socialId={dialogSocialId}
        socialOptions={socialList}
        onClose={() => {
          if (isSaving) return;
          setDialogOpen(false);
          setDialogInitial(null);
        }}
        onSave={handleSave}
        isSaving={isSaving}
      />
      <AdminConfirmDialog
        open={isDeleteDialogOpen}
        title="Delete Social Media Update"
        message={deleteTarget?.name ? `Are you sure you want to delete this update${deleteTarget.name.trim() ? `: "${deleteTarget.name}"` : ''}? This action cannot be undone.` : 'Are you sure you want to delete this social media update?'}
        confirmLabel="Delete"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        isProcessing={isDeleting}
      />
    </div>
  );
};

export default AdminSocialMediaUpdates;
